import React from 'react';
import { compose } from 'redux';
import { Route } from 'react-router-dom';

import WebGalleriesMasterView from '../views/WebGalleriesMasterView';
import WebGalleriesMainView from '../views/WebGalleriesMainView';
import WebGalleriesAlbumView from '../views/WebGalleryAlbumView';
import WebGalleriesPhotoGroupView from '../views/WebGalleriesPhotoGroupView';
import WebGalleriesFolderView from '../views/WebGalleriesFolderView';
import WebGalleriesDiscoveryView from '../views/WebGalleriesDiscoveryView';
import RequireWebGalleryFolderPermissions from '../hoc/RequireWebGalleryFolderPermissions';
import RequireWebGalleryAlbumPermissions from '../hoc/RequireWebGalleryAlbumPermissions';
import WebGalleryFolderCodeRedirect from '../hoc/WebGalleryFolderCodeRedirect';
import RedirectWithStatus from '../../../components/routing/RedirectWithStatus';
import WebGalleriesFavoritePhotosView from '../views/WebGalleriesFavoritePhotosView';
import WebGalleryAlbumFolderAccessControl from '../hoc/WebGalleryAlbumFolderAccessControl';
import WebGalleryAccessRequestView from '../views/WebGalleryAccessRequestView';
import RequireAuth from '../../../components/routing/RequireAuth';
import { AUTH_TYPE } from '../../../constants/auth';
import WebGalleryBlogListingView from '../views/WebGalleryBlogListingView';
import WebGalleriesBlogPostView from '../views/WebGalleriesBlogPostView';
import WebGalleriesAlbumWaldogramView from '../views/WebGalleriesAlbumWaldogramView';
import WebGalleriesAlbumCustomPrintCheckoutView from '../views/WebGalleriesAlbumCustomPrintCheckoutView';
import WebGalleryPasswordEntry from '../views/WebGalleryPasswordEntry';
import WebGalleryNoAccess from '../views/WebGalleryNoAccess';
import WebGalleryVideoListing from '../views/WebGalleryVideoListing';
import WebGalleryOnboardingRedirect from '../views/WebGalleryOnboardingRedirect';
import WebGalleryAccountCards from '../views/WebGalleryAccountCards';
import AlbumSearchView from '../views/AlbumSearch/AlbumSearchView';
import WebGalleryAlbumVideoListing from '../views/WebGalleryAlbumVideoListing/WebGalleryAlbumVideoListing';
import WebGalleryAlbumContainer from '../components/WebGalleryAlbum/WebGalleryAlbumContainer';
import WebGalleryPhotoGroupContainer from '../components/WebGalleryPhotoGroup/WebGalleryPhotoGroupContainer';
import WebGalleryFaces from '../views/WebGalleryFaces/WebGalleryFaces';

const routes = [
  {
    path: '/galleries/search',
    exact: true,
    component: AlbumSearchView,
  },
  {
    // Redirect any /galleries/username routes to /@username
    path: '/galleries/:username',
    exact: true,
    component: () => (
      <Route
        path="/galleries/:username"
        exact
        render={({ match }) => (
          <RedirectWithStatus status={301} to={`/@${match.params.username}`} />
        )}
      />
    ),
  },
  {
    path: '/galleries/o/album/:albumId',
    exact: true,
    component: WebGalleryOnboardingRedirect,
  },
  {
    path: '/galleries/o/album-folder/:albumFolderId',
    exact: true,
    component: WebGalleryOnboardingRedirect,
  },
  {
    path: '/galleries/password/:recordId',
    exact: true,
    component: WebGalleryPasswordEntry,
  },
  {
    path: '/galleries/no-access/:recordId',
    exact: true,
    component: WebGalleryNoAccess,
  },
  {
    path: '/:slug?/galleries',
    exact: false,
    component: WebGalleriesMasterView,
    routes: [
      {
        path: '/:slug?/galleries',
        exact: true,
        component: WebGalleriesDiscoveryView,
      },
      {
        path: '/:slug?/galleries/view/folder/:folderId/blog/:blogPostId',
        exact: true,
        component: WebGalleriesBlogPostView,
      },
      {
        path: '/:slug?/galleries/view/album/:albumId/blog/:blogPostId',
        exact: true,
        component: WebGalleriesBlogPostView,
      },
      {
        path:
          '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId/blog/:blogPostId',
        exact: true,
        component: WebGalleriesBlogPostView,
      },
      {
        path: '/:slug?/galleries/albums/:albumId/postcard',
        exact: true,
        component: RequireAuth(WebGalleriesAlbumWaldogramView, {
          authType: AUTH_TYPE.PHONE,
        }),
      },
      {
        path: '/:slug?/galleries/postcards/create',
        exact: true,
        component: RequireAuth(WebGalleryAccountCards, {
          authType: AUTH_TYPE.PHONE,
        }),
      },
      {
        path: '/:slug?/galleries/view',
        exact: false,
        component: WebGalleriesMainView,
        routes: [
          {
            path: '/:slug?/galleries/view/folder/:folderId/request-access',
            exact: true,
            component: RequireAuth(WebGalleryAccessRequestView, {
              authType: AUTH_TYPE.PHONE,
            }),
          },
          {
            path: '/:slug?/galleries/view/folder/:folderId',
            exact: true,
            component: compose(
              WebGalleryFolderCodeRedirect,
              RequireWebGalleryFolderPermissions,
              WebGalleryAlbumFolderAccessControl,
            )(WebGalleriesFolderView),
          },
          {
            path: '/:slug?/galleries/view/folder/:folderId/favorites',
            exact: false,
            component: RequireWebGalleryFolderPermissions(
              WebGalleriesFavoritePhotosView,
            ),
          },
          {
            path: '/:slug?/galleries/view/album/:albumId/favorites',
            exact: false,
            component: RequireWebGalleryAlbumPermissions(
              WebGalleriesFavoritePhotosView,
            ),
          },
          {
            path: '/:slug?/galleries/view/folder/:folderId/blog',
            exact: true,
            component: WebGalleryBlogListingView,
          },
          {
            path: '/:slug?/galleries/view/folder/:folderId/videos',
            exact: false,
            component: WebGalleryVideoListing,
          },
          {
            path: '/:slug?/galleries/view/album/:albumId/blog',
            exact: true,
            component: WebGalleryBlogListingView,
          },
          {
            path:
              '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId/blog',
            exact: true,
            component: WebGalleryBlogListingView,
          },

          {
            path:
              '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId',
            exact: false,
            component: WebGalleriesPhotoGroupView,
            routes: [
              {
                path:
                  '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId/faces',
                exact: true,
                component: WebGalleryFaces,
              },
              {
                path:
                  '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId/videos',
                exact: false,
                component: WebGalleryAlbumVideoListing,
              },
              {
                path:
                  '/:slug?/galleries/view/album/:albumId/subalbums/:photoGroupId',
                exact: false,
                component: WebGalleryPhotoGroupContainer,
              },
            ],
          },
          {
            path: '/:slug?/galleries/view/album/:albumId',
            exact: false,
            component: RequireWebGalleryAlbumPermissions(WebGalleriesAlbumView),
            routes: [
              {
                path: '/:slug?/galleries/view/album/:albumId/faces',
                exact: true,
                component: WebGalleryFaces,
              },
              {
                path: '/:slug?/galleries/view/album/:albumId/videos',
                exact: false,
                component: WebGalleryAlbumVideoListing,
              },
              {
                path: '/:slug?/galleries/view/album/:albumId',
                exact: false,
                component: WebGalleryAlbumContainer,
              },
            ],
          },
          {
            path: '/:slug?/galleries/view/album/:albumId',
            exact: false,
            component: RequireWebGalleryAlbumPermissions(WebGalleriesAlbumView),
          },
          {
            path:
              '/:slug?/galleries/view/album/:albumId/custom-prints/checkout',
            exact: true,
            component: WebGalleriesAlbumCustomPrintCheckoutView,
          },
        ],
      },
      {
        // This is a bit misleading as :folderId can be a join code too
        // but we're keeping the path param name as it is for better
        // compatibility with containers that already depend on it being
        // named `folderId`. A better name would be `:folderIdOrJoinCode`.
        // This path is just a redirect now, /vmasto?/galleries/mygallery ->
        // /galleries/view/folder/$myGalleryId
        path: '/:slug?/galleries/:folderId',
        exact: true,
        component: compose(
          WebGalleryFolderCodeRedirect,
          RequireWebGalleryFolderPermissions,
        )(WebGalleriesFolderView),
      },
    ],
  },
  {
    // Shortlink redirect
    path: '/:slug?/g/:folderId',
    exact: true,
    component: () => (
      <Route
        path="/:slug?/g/:folderId"
        exact
        render={({ location }) => (
          <RedirectWithStatus
            status={301}
            to={location.pathname.replace('/g/', '/galleries/')}
          />
        )}
      />
    ),
  },
];

export default routes;
